import React, { useState, useEffect, useRef } from "react";
import { makeStyles, createStyles, useTheme } from "@material-ui/core";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import clsx from "clsx";
import FormatColorFillIcon from "@material-ui/icons/FormatColorFill";
import ReactDOM from "react-dom";
import { CustomTheme } from "../../../../types/customTheme";

interface Props {
  backgroundColor: any;
}

const useStyles = makeStyles<any, Props>((theme: CustomTheme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.background.paper,
      margin: "16px",
    },
    colorPickerModal: {
      border: "none",
      "& .rdw-colorpicker-modal": {
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: "10px",
        border: "none",
        boxShadow: "none",
      },
      "& .rdw-editor-main": {
        backgroundColor: ({ backgroundColor }) => backgroundColor,
        borderRadius: "10px",
      },
      "& .rdw-dropdown-wrapper": {
        color: "black",
      },
      "& .rdw-dropdownoption-default": {
        color: "black",
      },
    },
    toolbarWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      ".rdw-editor-toolbar": {
        flex: 1,
      },
    },
    customColorInput: {
      width: 36,
      height: 30,
      border: "none",
      background: "none",
      cursor: "pointer",
      marginLeft: 8,
    },
  })
);

export interface TextEditorProps {
  editorState: any;
  handleChangeEditorState: (editorState: any) => void;
  setBackgroundColor: (color: string) => void;
  backgroundColor: string;
  minHeight: any;
}

const TextEditor: React.FC<TextEditorProps> = ({
  editorState,
  handleChangeEditorState,
  setBackgroundColor,
  backgroundColor,
  minHeight,
}) => {
  const theme = useTheme();
  const editorRef = useRef<HTMLDivElement>(null);

  const classes = useStyles({ backgroundColor });

  useEffect(() => {
    const toolbar = editorRef.current?.querySelector(".rdw-editor-toolbar");

    const updateTooltip = () => {
      const colorBtn = toolbar?.querySelector(".rdw-colorpicker-wrapper");
      if (colorBtn && colorBtn.getAttribute("title") !== "Text Color") {
        colorBtn.setAttribute("title", "Text Color");
      }
    };

    const observer = new MutationObserver(updateTooltip);
    if (toolbar) {
      observer.observe(toolbar, { childList: true, subtree: true });
      updateTooltip();
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const moveTextColorBeforeBackground = () => {
      const toolbar = editorRef.current?.querySelector(".rdw-editor-toolbar");
      const textColorPicker = toolbar?.querySelector(
        ".rdw-colorpicker-wrapper"
      );
      const backgroundColorPicker = toolbar?.querySelector(
        ".custom-color-picker-wrapper"
      );

      if (toolbar && textColorPicker && backgroundColorPicker) {
        toolbar.insertBefore(textColorPicker, backgroundColorPicker);
      }
    };

    const timeoutId = setTimeout(moveTextColorBeforeBackground, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const toolbarEl = editorRef.current?.querySelector(".rdw-editor-toolbar");
    const existingWrapper = toolbarEl?.querySelector(
      ".custom-color-picker-wrapper"
    );

    if (toolbarEl && !existingWrapper) {
      const wrapper = document.createElement("div");
      wrapper.className = "rdw-option-wrapper custom-color-picker-wrapper";
      wrapper.style.position = "relative";
      wrapper.style.width = "28px";
      wrapper.style.height = "15px";
      wrapper.style.display = "flex";
      wrapper.style.alignItems = "center";
      wrapper.style.justifyContent = "center";
      wrapper.style.cursor = "pointer";
      wrapper.style.paddingTop = "10px";
      wrapper.title = "Background Color";

      const renderPicker = (color: string) => {
        const iconContainer = document.createElement("div");
        iconContainer.style.pointerEvents = "none";
        ReactDOM.render(
          <FormatColorFillIcon style={{ fontSize: 20, color }} />,
          iconContainer
        );

        const input = document.createElement("input");
        input.type = "color";
        input.value = color;
        input.className = "custom-color-picker";
        input.style.position = "absolute";
        input.style.top = "0";
        input.style.left = "0";
        input.style.width = "36px";
        input.style.height = "36px";
        input.style.opacity = "0";
        input.style.cursor = "pointer";

        input.oninput = (e: any) => {
          const newColor = e.target.value;
          setBackgroundColor(newColor);

          setTimeout(() => {
            while (wrapper.firstChild) wrapper.removeChild(wrapper.firstChild);
            renderPicker(newColor);
          }, 100);
        };

        wrapper.appendChild(input);
        wrapper.appendChild(iconContainer);
      };

      renderPicker(backgroundColor);
      toolbarEl.appendChild(wrapper);
    }
  }, [backgroundColor, setBackgroundColor]);

  return (
    <div
      className={clsx(classes.root, classes.colorPickerModal)}
      ref={editorRef}
    >
      <Editor
        editorState={editorState}
        onEditorStateChange={handleChangeEditorState}
        editorStyle={{
          minHeight: minHeight,
          border: "1px solid",
          padding: "4px",
        }}
        toolbarStyle={{
          backgroundColor: theme.palette.background.paper,
          border: "0px",
        }}
        toolbarClassName="rdw-editor-toolbar"
        wrapperClassName="wrapperClassName"
        editorClassName="editorClassName"
        toolbar={{
          options: ["inline", "fontSize", "colorPicker", "list", "link"],
          inline: {
            inDropdown: false,
            options: ["bold", "italic", "underline", "strikethrough"],
          },
          // Add a colorPicker object to customize the available colors
          colorPicker: {
            colors: [
              "rgb(0, 0, 0)",
              "rgb(255, 255, 255)",
              "rgb(255, 0, 0)",
              "rgb(255, 153, 0)",
              "rgb(255, 255, 0)",
              "rgb(0, 255, 0)",
              "rgb(0, 255, 255)",
              "rgb(0, 0, 255)",
              "rgb(153, 0, 255)",
              "rgb(255, 0, 255)",
              "rgb(204, 204, 204)",
              "rgb(102, 102, 102)",
              "rgb(255, 102, 102)",
              "rgb(255, 204, 153)",
              "rgb(255, 255, 153)",
              "rgb(204, 255, 153)",
              "rgb(153, 255, 255)",
              "rgb(153, 204, 255)",
              "rgb(204, 153, 255)",
              "rgb(255, 153, 255)",
              "rgb(255, 204, 204)",
              "rgb(255, 255, 204)",
              "rgb(204, 255, 204)",
              "rgb(204, 255, 255)",
              "rgb(204, 204, 255)",
              "rgb(255, 204, 255)",
              "rgb(153, 51, 0)",
              "rgb(102, 51, 0)",
              "rgb(51, 102, 0)",
              "rgb(0, 51, 102)",
              "rgb(51, 0, 102)",
              "rgb(102, 0, 51)",
              "rgb(51, 51, 0)",
              "rgb(0, 51, 51)",
              "rgb(51, 0, 51)",
              "rgb(102, 102, 51)",
              "rgb(51, 102, 102)",
              "rgb(51, 51, 102)",
              "rgb(102, 51, 102)",
            ],
          },
        }}
        localization={{
          locale: "en",
          translations: {
            "components.controls.colorpicker.color": "Text Color",
          },
        }}
      />
    </div>
  );
};

export default TextEditor;